import { Head, Link } from '@inertiajs/react'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Share2 } from 'lucide-react'
import { SiteLayout } from '@/components/site-layout'
import { Button } from '@/components/ui/button'
import { BaselineAppModal } from '@/components/baseline-app-modal'
import { apiFetch } from '@/lib/utils'

// ── Types ────────────────────────────────────────────────────────────────────

type Nominee = {
  id: string
  name: string
  bio: string
  title: string
  content: string
  votes_count: number
  photo_url: string
  voted: boolean
  sponsor?: boolean
}

type Props = {
  id: string
  nominee?: Nominee | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const API_BASE = 'https://baseline.live/api/v2'

// ── Animation variants ────────────────────────────────────────────────────────

const fadeUp = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0 } }
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } }

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DeCuShow({ id, nominee: initialNominee }: Props) {
  const [nominee, setNominee] = useState<Nominee | null>(initialNominee ?? null)
  const [loading, setLoading] = useState(!initialNominee)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showSticky, setShowSticky] = useState(false)
  const [atBottom, setAtBottom] = useState(false)
  const [categoryRank, setCategoryRank] = useState<{ rank: number } | null>(null)

  useEffect(() => {
    const onScroll = () => {
      setShowSticky(window.scrollY > 300)
      const distFromBottom = document.documentElement.scrollHeight - window.innerHeight - window.scrollY
      setAtBottom(distFromBottom < 16)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  async function handleShare() {
    if (!nominee) return
    const url = window.location.href
    if (navigator.share) {
      await navigator.share({
        title: `${nominee.name} — Vietnam Pickleball Awards 2025`,
        text: nominee.bio,
        url,
      })
    } else {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  useEffect(() => {
    if (initialNominee) return
    async function loadNominee() {
      try {
        const res = await apiFetch(`${API_BASE}/voting_nominees/${id}`)
        if (!res.ok) throw new Error('Không thể tải thông tin đề cử.')
        const data = await res.json()
        setNominee(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Đã có lỗi xảy ra.')
      } finally {
        setLoading(false)
      }
    }
    loadNominee()
  }, [id])

  useEffect(() => {
    if (!nominee) return
    const currentNominee = nominee
    let cancelled = false
    async function loadRank() {
      try {
        // The nominee API returns section_id but not category_id directly.
        // Try direct category fields first, then fall back to scanning the section.
        const categoryId = (currentNominee as any).voting_category_id ?? (currentNominee as any).category?.id
        if (categoryId) {
          const res = await apiFetch(`${API_BASE}/voting_categories/${categoryId}`)
          if (!res.ok) return
          const data = await res.json()
          const nominees: Array<{ id: string; votes_count: number }> =
            data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
          const sorted = [...nominees].sort((a, b) => b.votes_count - a.votes_count)
          const myVotes = currentNominee.votes_count
          const rank = sorted.findIndex((n) => n.votes_count === myVotes) + 1
          if (rank > 0) {
            if (!cancelled) setCategoryRank({ rank })
          }
          return
        }

        // Fallback: fetch the section to discover category IDs, then find which
        // category contains this nominee.
        const sectionId = (currentNominee as any).section_id
        if (!sectionId) return
        const sectionRes = await apiFetch(`${API_BASE}/voting_sections/${sectionId}`)
        if (!sectionRes.ok) return
        const sectionData = await sectionRes.json()
        const cats: Array<{ id: string }> =
          sectionData.voting_categories ?? sectionData.categories ?? []
        for (const cat of cats) {
          const catRes = await apiFetch(`${API_BASE}/voting_categories/${cat.id}`)
          if (!catRes.ok) continue
          const catData = await catRes.json()
          const nominees: Array<{ id: string; votes_count: number }> =
            catData.voting_nominees ?? catData.nominees ?? catData.data?.nominees ?? []
          const found = nominees.some((n) => n.id === currentNominee.id)
          if (found) {
            const sorted = [...nominees].sort((a, b) => b.votes_count - a.votes_count)
            const myVotes = currentNominee.votes_count
            const rank = sorted.findIndex((n) => n.votes_count === myVotes) + 1
            if (rank > 0) {
              if (!cancelled) setCategoryRank({ rank })
            }
            return
          }
        }
      } catch {
        // silently ignore — rank is non-critical
      }
    }
    loadRank()
    return () => { cancelled = true }
  }, [nominee?.id])

  return (
    <>
    <SiteLayout>
      <Head title={nominee ? `${nominee.name} — Vietnam Pickleball Awards 2025` : 'Đề cử — Vietnam Pickleball Awards 2025'}>
        {nominee && (
          <>
            <meta name="description" content={nominee.bio || nominee.title} />
            <meta property="og:type" content="profile" />
            <meta property="og:site_name" content="Vietnam Pickleball Awards 2025" />
            <meta property="og:locale" content="vi_VN" />
            <meta property="og:title" content={`${nominee.name} — ${nominee.title}`} />
            <meta property="og:description" content={nominee.bio || nominee.title} />
            {nominee.photo_url && <meta property="og:image" content={nominee.photo_url} />}
            {nominee.photo_url && <meta property="og:image:width" content="1200" />}
            {nominee.photo_url && <meta property="og:image:height" content="1200" />}
            {nominee.photo_url && <meta property="og:image:alt" content={nominee.name} />}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${nominee.name} — ${nominee.title}`} />
            <meta name="twitter:description" content={nominee.bio || nominee.title} />
            {nominee.photo_url && <meta name="twitter:image" content={nominee.photo_url} />}
          </>
        )}
      </Head>

      <section className="mx-auto max-w-3xl px-4 pt-6 sm:px-6 sm:pt-8">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-[#5b7788] text-xs uppercase tracking-[0.2em] transition hover:text-[#042133]"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          Trang chủ
        </Link>

        {loading && (
          <div className="mt-8 space-y-4">
            <div className="aspect-[4/3] w-full animate-pulse rounded-2xl bg-[#042133]/8 sm:aspect-[16/9]" />
            <div className="h-8 w-3/4 animate-pulse rounded-full bg-[#042133]/8" />
            <div className="h-4 w-full animate-pulse rounded-full bg-[#042133]/8" />
            <div className="h-4 w-5/6 animate-pulse rounded-full bg-[#042133]/8" />
          </div>
        )}

        {error && (
          <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && nominee && (
          <div className="mt-6 pb-20">
            {/* Full-width hero photo with overlay */}
            {nominee.photo_url && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="relative -mx-4 overflow-hidden sm:-mx-6 md:mx-0 md:rounded-2xl"
              >
                <img
                  src={nominee.photo_url}
                  alt={nominee.name}
                  className="h-auto max-h-[70vh] w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#042133]/70 via-[#042133]/10 to-transparent" />
                {/* Category badge — top-left */}
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
                  className="absolute left-4 top-4 sm:left-6 sm:top-6"
                >
                  <span className="inline-block rounded-full bg-black/30 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                    {nominee.title}
                  </span>
                </motion.div>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.5, delay: 0.4, ease: 'easeOut' }}
                  className="absolute right-0 bottom-0 left-0 p-5 sm:p-8"
                >
                  <p className="text-white/70 text-[10px] uppercase tracking-[0.3em]">
                    Vietnam Pickleball Awards 2025
                  </p>
                  <h1 className="mt-1 font-display font-semibold text-2xl leading-snug tracking-tight text-white sm:text-3xl md:text-4xl">
                    {nominee.name}
                  </h1>
                  {nominee.bio && (
                    <p className="mt-2 text-white/80 text-sm leading-relaxed sm:text-base">
                      {nominee.bio}
                    </p>
                  )}
                </motion.div>
              </motion.div>
            )}

            {/* Fallback header if no photo */}
            {!nominee.photo_url && (
              <div className="mt-0">
                <p className="text-[#5b7788] text-[10px] uppercase tracking-[0.3em]">
                  Vietnam Pickleball Awards 2025
                </p>
                <h1 className="mt-2 font-display font-semibold text-2xl leading-snug tracking-tight sm:text-3xl md:text-4xl">
                  {nominee.name}
                </h1>
                {nominee.bio && (
                  <p className="mt-3 font-medium text-[#5b7788] text-sm leading-relaxed sm:text-base">
                    {nominee.bio}
                  </p>
                )}
              </div>
            )}

            {/* Vote bar */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.4, delay: 0.55, ease: 'easeOut' }}
              className="mt-5 rounded-2xl border border-[#042133]/10 bg-white px-5 py-3.5 shadow-sm"
            >
              {categoryRank && !nominee.sponsor && (
                <p className="mb-2.5 text-center text-[11px] font-semibold text-[#042133]">
                  #{categoryRank.rank} · {nominee.votes_count} phiếu
                </p>
              )}
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  aria-label="Chia sẻ trang này"
                  className="flex shrink-0 items-center gap-1.5 rounded-full border-[#042133]/20 px-4 py-2 font-semibold text-[#042133] text-xs uppercase tracking-[0.1em] hover:bg-[#042133]/5"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {copied ? 'Đã sao chép!' : 'Chia sẻ'}
                </Button>
                {!nominee.sponsor && (
                  <Button
                    onClick={() => setModalOpen(true)}
                    className="flex-1 whitespace-normal h-auto text-center rounded-full bg-[#FCC632] px-4 py-2 font-semibold text-[#042133] text-xs uppercase tracking-[0.06em] hover:bg-[#FCC632]/90"
                  >
                    <span className="sm:hidden">Bình chọn</span><span className="hidden sm:inline">Bình chọn tại Baseline ▶</span>
                  </Button>
                )}
              </div>
            </motion.div>

            {/* Content */}
            {nominee.content && (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.4, delay: 0.65, ease: 'easeOut' }}
                className="mt-8 rounded-2xl bg-white/70 px-5 py-6 shadow-sm ring-1 ring-[#042133]/5 sm:px-7 sm:py-8"
              >
                <div className="prose prose-sm max-w-none text-[#5b7788] sm:prose-base prose-p:leading-relaxed prose-img:rounded-2xl prose-img:w-full prose-img:my-6 prose-headings:text-[#042133] prose-headings:font-semibold prose-strong:text-[#042133]">
                  <ReactMarkdown>{nominee.content}</ReactMarkdown>
                </div>
              </motion.div>
            )}

            {/* Bottom CTA */}
            {!nominee.sponsor && (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.4, delay: 0.75, ease: 'easeOut' }}
                className="mt-10 flex justify-center"
              >
                <Button
                  onClick={() => setModalOpen(true)}
                  className="rounded-full bg-[#FCC632] px-8 py-2.5 font-semibold text-[#042133] text-sm uppercase tracking-[0.15em] hover:bg-[#FCC632]/90"
                >
                  Tải app Baseline để bình chọn
                </Button>
              </motion.div>
            )}
          </div>
        )}
      </section>

      <BaselineAppModal open={modalOpen} onOpenChange={setModalOpen} />

    </SiteLayout>

      {/* Sticky bottom bar — portalled to body to escape stacking context */}
      {nominee && createPortal(
        <div
          aria-hidden={!showSticky}
          className={`fixed bottom-0 left-0 right-0 z-50 flex items-center gap-3 bg-[#042133] px-4 py-3 shadow-lg transition-transform duration-300 sm:bottom-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:rounded-full sm:px-6 ${showSticky && !atBottom ? 'translate-y-0' : 'translate-y-full'}`}
        >
          <span className="hidden sm:block min-w-0 flex-1 truncate font-semibold text-sm text-white">
            {nominee.name}
          </span>
          <div className="flex flex-1 sm:flex-none items-center gap-2">
            <Button
              variant="outline"
              onClick={handleShare}
              aria-label="Chia sẻ trang này"
              className="h-8 shrink-0 rounded-full border-white/30 bg-transparent px-3 font-semibold text-white text-xs uppercase tracking-[0.1em] hover:bg-white/10"
            >
              <Share2 className="h-3.5 w-3.5" />
              <span className="ml-1.5 hidden sm:inline">{copied ? 'Đã sao chép!' : 'Chia sẻ'}</span>
            </Button>
            {!nominee.sponsor && (
              <Button
                onClick={() => setModalOpen(true)}
                className="flex-1 h-auto whitespace-normal text-center rounded-full bg-[#FCC632] px-4 py-1.5 font-semibold text-[#042133] text-xs uppercase tracking-[0.06em] hover:bg-[#FCC632]/90"
              >
                <span className="sm:hidden">Bình chọn</span><span className="hidden sm:inline">Bình chọn tại Baseline ▶</span>
              </Button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
